import {
  QUERY_CREATE_RESTRICTION,
  QUERY_REMOVE_RESTRICTION,
  QUERY_REMOVE_RESTRICTIONS,
  QUERY_RESTRICTION_AND_CURB_ZONES,
  QUERY_UPDATE_RESTRICTION,
  QUERY_UPDATE_RESTRICTIONS,
} from "@/services/web/api-restriction.const";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";
import type { Range } from "react-date-range";
import { convertDateToUTC } from "@/helper/date-time";
import { skipToken } from "@apollo/client/react";
import { useContext } from "react";
import { useDebounce } from "@/hooks/lib-ui";
import { useMemo } from "react";
import { useSearchFilterCurbZone } from "@/hooks/use-search-filter-curb-zone";
import { useState } from "react";

export const useQueryRestriction = ({ selectedDate }: { selectedDate: Range }) => {
  const { currentCity } = useContext(CityContext);
  const [bounding, setBounding] = useState<number[][]>();
  const nearBy = useDebounce(bounding, 500);
  const { filter } = useSearchFilterCurbZone();

  const variables = useMemo(() => {
    /**
     * Format datetime by city's iana
     * */
    if (!currentCity?.id || !nearBy) {
      return undefined;
    }
    const startDate = selectedDate?.startDate ? convertDateToUTC(selectedDate.startDate) : undefined;
    const endDate = selectedDate?.endDate ? convertDateToUTC(selectedDate.endDate) : undefined;

    return {
      filterInputCurbZone: {
        cityId: currentCity.id,
        boundingbox: nearBy,
        policies: filter.selectedPolicies.length > 0 ? filter.selectedPolicies : undefined,
      },
      filterInputRestriction: {
        cityId: currentCity.id,
        boundingbox: nearBy,
        policies: filter.selectedPolicies.length > 0 ? filter.selectedPolicies : undefined,
        startDate,
        endDate,
      },
    };
  }, [nearBy, filter.selectedPolicies, selectedDate, currentCity?.id]);

  const queryOptions = useMemo(
    () =>
      variables
        ? {
            actionName: "CurbZonesAndRestrictions",
            variables,
          }
        : skipToken,
    [variables],
  );

  const { data, loading, refetch } = useTrackedQuery(QUERY_RESTRICTION_AND_CURB_ZONES, queryOptions);

  const refetchQueriesConfig = useMemo(
    () => ({
      refetchQueries: [{ query: QUERY_RESTRICTION_AND_CURB_ZONES, variables }],
      awaitRefetchQueries: true,
    }),
    [variables],
  );

  const [createRestriction] = useTrackedMutation(QUERY_CREATE_RESTRICTION, {
    actionName: "CreateRestriction",
    ...refetchQueriesConfig,
  });

  const [createRestrictionWithoutRefetch] = useTrackedMutation(QUERY_CREATE_RESTRICTION, {
    actionName: "CreateRestriction",
  });

  const [updateRestriction] = useTrackedMutation(QUERY_UPDATE_RESTRICTION, {
    actionName: "UpdateRestriction",
    ...refetchQueriesConfig,
  });

  const [removeRestriction] = useTrackedMutation(QUERY_REMOVE_RESTRICTION, {
    actionName: "DeleteRestriction",
    ...refetchQueriesConfig,
  });

  const [removeMultiRestriction] = useTrackedMutation(QUERY_REMOVE_RESTRICTIONS, {
    actionName: "BulkDeleteRestrictions",
    ...refetchQueriesConfig,
  });

  const [updateMultiRestriction] = useTrackedMutation(QUERY_UPDATE_RESTRICTIONS, {
    actionName: "BulkUpdateRestrictions",
    ...refetchQueriesConfig,
  });

  return {
    bounding,
    setBounding,
    data: {
      restrictionsByBounding: data?.restrictionsByBounding ?? [],
      curbZones: data?.curbZones ?? [],
    },
    loading,
    createRestriction,
    createRestrictionWithoutRefetch,
    updateRestriction,
    updateMultiRestriction,
    removeMultiRestriction,
    removeRestriction,
    refetch,
    nearBy,
  };
};
